import React, { useEffect, Suspense, lazy } from "react";
import { Routes, Route, useLocation, matchPath } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import NavBar from "./components/NavBar";
import Footer from "./components/Footer";
import HeroSectionNew from "./components/HeroSectionNew";
import ToTopOverlay from "./components/ToTopOverlay";
import PrivateRoute from "./PrivateRoute";
import SpinnerPage from "./components/SpinnerPage";
import ScrollToTop from "./components/ScrollToTop";

// Lazy-loaded pages
const StartPage = lazy(() => import("./pages/StartPage"));
const TreatmentsPage = lazy(() => import("./pages/TreatmentsPage"));
const PricesPage = lazy(() => import("./pages/PricesPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const AboutUsPage = lazy(() => import("./pages/AboutUsPage"));
const EditTreatment = lazy(() => import("./pages/EditTreatment"));
const CreateTreatment = lazy(() => import("./pages/CreateTreatment"));
const EditTreatments = lazy(() => import("./pages/EditTreatments"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const CreateReviewSession = lazy(() => import("./pages/CreateReviewSession"));
const ReviewFormPage = lazy(() => import("./pages/ReviewFormPage"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const EditEmployees = lazy(() => import("./pages/EditEmployees"));
const EditArticles = lazy(() => import("./pages/EditArticles"));
const EditInformationSegment = lazy(() => import("./pages/EditInformationSegment"));
const EditImages = lazy(() => import("./pages/EditImages"));
const EditShop = lazy(() => import("./pages/EditShop"));
const EditOverrideWorkingHours = lazy(() => import("./pages/EditOverrideWorkingHours"));
const EditWorkingHours = lazy(() => import("./pages/EditWorkingHours"));
const MyCalendar = lazy(() => import("./components/MyCalendar"));
const BookingTest = lazy(() => import("./components/BookingTest"));
const BookingFetcher = lazy(() => import("./components/BookingFetcher"));
const EditVideos = lazy(() => import("./pages/EditVideos"));
const CreateArticle = lazy(() => import("./pages/CreateArticle"));
const CreateInformationSegment = lazy(() => import("./pages/CreateInformationSegment"));
const EmbeddedBookingPage = lazy(() => import("./pages/EmbeddedBookingPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const routes = [
  { path: "/", element: <StartPage />, showHero: true },
  { path: "/start", element: <StartPage />, showHero: true },
  { path: "/treatments", element: <TreatmentsPage />, showHero: true },
  { path: "/prices", element: <PricesPage />, showHero: true },
  { path: "/booking", element: <BookingPage />, showHero: true },
  { path: "/about-us", element: <AboutUsPage />, showHero: true },
  { path: "/review", element: <ReviewFormPage />, showHero: true },
  { path: "/booking-external", element: <EmbeddedBookingPage />, showHero: false },

  // Admin-only routes
  { path: "/create-review-session", element: <CreateReviewSession />, private: true, showHero: false },
  { path: "/edit-treatment/:id", element: <EditTreatment />, private: true, showHero: false },
  { path: "/edit-images", element: <EditImages />, private: true, showHero: false },
  { path: "/edit-employees", element: <EditEmployees />, private: true, showHero: false },
  { path: "/my-calendar", element: <MyCalendar />, private: true, showHero: false },
  { path: "/booking-test", element: <BookingTest />, private: true, showHero: false },
  { path: "/booking-fetcher", element: <BookingFetcher />, private: true, showHero: false },
  { path: "/create-article", element: <CreateArticle />, private: true, showHero: false },
  { path: "/edit-articles", element: <EditArticles />, private: true, showHero: false },
  { path: "/create-information-segment", element: <CreateInformationSegment />, private: true, showHero: false },
  { path: "/edit-information-segment", element: <EditInformationSegment />, private: true, showHero: false },
  { path: "/create-treatment", element: <CreateTreatment />, private: true, showHero: false },
  { path: "/edit-treatments", element: <EditTreatments />, private: true, showHero: false },
  { path: "/edit-override-working-hours", element: <EditOverrideWorkingHours />, private: true, showHero: false },
  { path: "/edit-shop", element: <EditShop />, private: true, showHero: false },
  { path: "/edit-working-hours", element: <EditWorkingHours />, private: true, showHero: false },
  { path: "/edit-videos", element: <EditVideos />, private: true, showHero: false },

  // Special: reviews page (admin OR shopowner)
  { path: "/review-list", element: <ReviewsPage />, private: true, allowedRoles: ["admin", "shopowner"], showHero: false },

  { path: "/login", element: <LoginPage />, showHero: false },
  { path: "*", element: <NotFound />, showHero: false },
];

function AppContent() {
  const location = useLocation();

  const currentRoute = routes.find((route) =>
    matchPath({ path: route.path, end: true }, location.pathname)
  );
  const showHero = currentRoute?.showHero ?? false;

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/controllers/fetch_theme.php?id=1`
        );
        const data = await res.json();
        if (data?.data?.theme) {
          document.documentElement.setAttribute("data-theme", data.data.theme);
        }
      } catch (err) {
        console.error("Failed to load theme:", err);
      }
    };
    fetchTheme();
  }, []);

  return (
    <>
      <ScrollToTop />
      <div className="app-background">
        <NavBar />
        <div className="navbar-spacer" />
        <div className="full-container">
          <div className="content-container">
            {showHero && <HeroSectionNew />}
            <ToTopOverlay />
            <Suspense fallback={<SpinnerPage />}>
              <Routes>
                {routes.map((route, index) =>
                  route.private ? (
                    <Route
                      key={index}
                      path={route.path}
                      element={
                        <PrivateRoute allowedRoles={route.allowedRoles || ["admin"]}>
                          {route.element}
                        </PrivateRoute>
                      }
                    />
                  ) : (
                    <Route key={index} path={route.path} element={route.element} />
                  )
                )}
              </Routes>
            </Suspense>
          </div>
          <Footer src={"/banner_pink_flowers_2.png"} alt="Banner Image">
            <p className="footer-text">ThaiMassage & hälsa - 2024</p>
          </Footer>
        </div>
      </div>
    </>
  );
}

export default AppContent;
